import styled from '@emotion/styled';
import { mediaQuery } from '@styles/index';
import { Panel } from './shared';

export const BuilderPanel = styled(Panel)`
  ${mediaQuery.uptoMobile} {
    border-radius: 28px;
    padding: 24px 12px 16px;
  }
`;

export const EmptyPanel = styled(BuilderPanel)`
  display: flex;
  flex-direction: column;
  gap: 36px;
  padding: 36px 24px 24px;
  border-radius: 28px;

  ${mediaQuery.uptoMobile} {
    gap: 24px;
    padding: 24px 12px 16px;
    border-radius: 28px;
  }
`;

export const BuilderHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  width: 100%;

  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export const EmptyHintCard = styled.div`
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 70px;
  padding: 2px 15px;
  border-radius: 14px;
  background: #1e0609;
  border: 1px solid #4f0d18;

  ${mediaQuery.uptoMobile} {
    height: auto;
    width: 100%;
    padding: 10px 12px;
  }
`;

export const EmptyHintText = styled.p`
  margin: 0;
  width: 303px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 18px;
  font-weight: 400;
  line-height: 1.36;
  letter-spacing: 0.36px;

  ${mediaQuery.uptoMobile} {
    width: 100%;
    font-size: 14px;
    letter-spacing: 0.28px;
  }
`;

export const BuilderIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;

  h2 {
    margin-top: 0;
  }

  ${mediaQuery.uptoMobile} {
    gap: 14px;
    width: 100%;

    h2 {
      margin-top: 0;
      font-size: 32px;
      line-height: 1;
    }

    & > div:first-of-type {
      gap: 12px;
      font-size: 18px;

      img {
        width: 24px;
        height: 24px;
      }
    }
  }
`;

export const BuilderGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 619px) minmax(0, 455px);
  gap: 16px;
  margin-top: 36px;

  ${mediaQuery.uptoTablet} {
    grid-template-columns: 1fr;
  }

  ${mediaQuery.uptoMobile} {
    margin-top: 36px;
    gap: 16px;
  }
`;

export const InnerCard = styled.div`
  background: #211113;
  border: 1px solid #471b21;
  border-radius: 16px;
  padding: 16px;
`;

export const InviteCard = styled(InnerCard)`
  min-height: 258px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 60px rgba(167, 41, 50, 0.33);

  ${mediaQuery.uptoMobile} {
    min-height: 236px;
    padding: 16px 8px;
    justify-content: center;
  }
`;

export const EmptyCard = styled(InnerCard)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 258px;
  padding: 16px;
  box-shadow: inset 0 0 60px rgba(167, 41, 50, 0.33);

  ${mediaQuery.uptoTablet} {
    height: auto;
    min-height: 236px;
    flex-direction: column;
    padding: 20px 16px;
  }

  ${mediaQuery.uptoMobile} {
    padding: 16px 8px;
  }
`;

export const EmptyRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 144px;
  width: 100%;

  ${mediaQuery.uptoTablet} {
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }
`;

export const EmptyInvite = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const Stack = styled.div`
  display: flex;
  justify-content: center;
`;

export const StackAvatar = styled.img`
  width: 80px;
  height: 80px;
  margin-left: -45px;
  border-radius: 50%;

  &:first-of-type {
    margin-left: 0;
  }
`;

export const Hint = styled.p`
  margin: 0;
  padding: 1px 11px;
  border-radius: 16px;
  background: #6f1122;
  box-shadow: inset 0 0 8px #90112a;
  font-size: 12px;
  line-height: 20px;
`;

export const FilledHint = styled(Hint)`
  margin-top: 8px;
`;

export const FormRow = styled.form<{ $flush?: boolean }>`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  width: min(486px, 100%);
  margin-top: ${({ $flush }) => ($flush ? '0' : '20px')};

  ${mediaQuery.uptoMobile} {
    gap: 7px;
    width: 100%;
    align-items: flex-end;
  }
`;

export const Field = styled.label`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
`;

export const InputShell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 12px;
  background: #261113;
  border: 1px solid #66373b;
  border-radius: 8px;

  ${mediaQuery.uptoMobile} {
    min-width: 0;
    padding: 0 7px;
    gap: 4px;
  }
`;

export const Prefix = styled.span`
  font-size: 14px;
  font-weight: 400;
`;

export const Divider = styled.span`
  width: 1px;
  height: 24px;
  background: #66373b;
`;

export const PhoneInput = styled.input`
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #fff;
  font-size: 14px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.45);
  }
`;

export const AddButton = styled.button<{ $wide?: boolean }>`
  height: 48px;
  padding: ${({ $wide }) => ($wide ? '0 32px' : '0 24px')};
  border: 1px solid #eb3f60;
  border-radius: 8px;
  background: #9c1630;
  box-shadow: inset 0 0 15px #ff6184;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  ${mediaQuery.uptoMobile} {
    width: 89px;
    padding: 0 6px;
    font-size: 12px;
    letter-spacing: 0;
    flex-shrink: 0;
  }
`;

export const SquadCard = styled(InnerCard)`
  min-height: 258px;

  ${mediaQuery.uptoMobile} {
    min-height: 0;
    padding: 16px 8px 8px;
  }
`;

export const SquadLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;

  img {
    width: 18px;
    height: 18px;
  }

  ${mediaQuery.uptoMobile} {
    margin-bottom: 4px;
    padding: 0 10px;
  }
`;

export const MemberGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 59px);
  justify-content: space-between;
  gap: 20px 0;
  padding: 14px;
  border-radius: 16px;
  background: #1b0c0e;

  ${mediaQuery.uptoMobile} {
    grid-template-columns: repeat(4, 59px);
    justify-content: space-between;
    padding: 20px 14px;
    gap: 10px 0;

    & > :nth-child(n + 8):not(:last-child) {
      display: none;
    }
  }
`;

export const Member = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 59px;
  font-size: 14px;
  line-height: 20px;
  text-align: center;

  ${mediaQuery.uptoMobile} {
    width: 59px;
    font-size: 14px;
  }
`;

export const MoreAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #462a2e;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 20px;
    height: 34px;
  }

  ${mediaQuery.uptoMobile} {
    width: 48px;
    height: 48px;
  }
`;
